import { useCallback, useEffect, useRef, useState } from 'react';
import { useStateRef } from '../../../../_helpers/hooks/useStateRef';
import SiteMapOverlay from './site-map-overlay/SiteMapOverlay';

/**
 * SiteMapWrapper Component
 * Wrapper Component for SiteMap
 * Handles the opening of the SiteMap
 */
const SiteMapWrapper: React.FC<{
	exploreMoreLabel: string;
	children?: React.ReactNode;
}> = ({ exploreMoreLabel, children }) => {
	// SiteMap is opened state
	const [isOpened, setOpened] = useState<boolean>(false);
	// SiteMap  open animation (css transition) is ended state
	const [isOpenAnimationEnded, setOpenAnimationEnded] =
		useState<boolean>(false);
	// SiteMap opened height state and ref in string, when SiteMap is closed the full height is hided by the overlay
	const [openedHeight, openedHeightRef, setOpenedHeight] =
		useStateRef<string>('500px');

	// SiteMap wrapper ref, this div controls the height of siteMap
	const siteMapWrapperRef = useRef<HTMLDivElement>(null);

	// Handles Ctrl+F keydown and opens SiteMap
	const handleWindowKeyDown = useCallback((ev: KeyboardEvent) => {
		if (ev.key.toLowerCase() === 'f' && (ev.metaKey || ev.ctrlKey)) {
			setOpened(true);
		}
	}, []);

	// Sets SiteMap opened height if its changed
	const setSiteMapOpenedHeight = useCallback(() => {
		// eslint-disable-next-line @typescript-eslint/ban-ts-comment
		// @ts-ignore
		const currentHeight = siteMapWrapperRef.current.offsetHeight + 'px';
		if (openedHeightRef.current !== currentHeight) {
			setOpenedHeight(currentHeight);
		}
	}, [siteMapWrapperRef.current]);

	// Adds Event listeners when page init and removes them when page destroyed
	useEffect(() => {
		window.addEventListener('keydown', handleWindowKeyDown);
		window.addEventListener('resize', setSiteMapOpenedHeight);
		setSiteMapOpenedHeight();
		return () => {
			window.removeEventListener('resize', setSiteMapOpenedHeight);
			window.removeEventListener('keydown', handleWindowKeyDown);
		};
	}, [siteMapWrapperRef.current]);

	// Remove Even Listeners when SiteMap get opened and animation (transition) ended
	useEffect(() => {
		if (isOpenAnimationEnded) {
			setOpenedHeight('auto');
			window.removeEventListener('resize', setSiteMapOpenedHeight);
			window.removeEventListener('keydown', handleWindowKeyDown);
		}
	}, [isOpenAnimationEnded]);

	// Opens SiteMap when somebody focused in with key
	const handleKeyUpOnSiteMap = (ev: React.KeyboardEvent<HTMLDivElement>) => {
		const targetClassList = (ev.target as HTMLElement).classList;
		if (
			targetClassList.contains('menu-item') ||
			targetClassList.contains('footer-link')
		) {
			setOpened(true);
		}
	};

	return (
		<>
			<style title="site-map-css-style-sheet">
				{`.blk-ac-site-map {--site-map-opened-height: ${openedHeight};}`}
			</style>
			<div
				className={`blk-ac-site-map ${isOpened ? 'is-opened' : ''}`}
				onTransitionEnd={
					isOpenAnimationEnded ? undefined : () => setOpenAnimationEnded(true)
				}
			>
				<div
					className="site-map-wrapper"
					ref={siteMapWrapperRef}
					onKeyUp={handleKeyUpOnSiteMap}
				>
					{children}
				</div>
				{!isOpened && <SiteMapOverlay {...{ setOpened, exploreMoreLabel }} />}
			</div>
		</>
	);
};

export default SiteMapWrapper;
